import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useToastNotifications } from "../../features/toast-notification/toastNotificationSlice";
import { upsertGroupAsync, clearError, readMyGroupsAsync } from "../../features/group/groupSlice";
import {
  ClipboardIcon,
  ExclamationCircleIcon,
  InformationCircleIcon
} from "@heroicons/react/solid";
import { Link, useLocation, useHistory } from "react-router-dom";
import { getAdminsAsync } from "../../features/user/userSlice";
import slugify from "slugify";
import Multiselect from "../general/Multiselect";
import NextStepsItemForm from "./NextStepsItemForm";
import { CheckCircleIcon, ClockIcon, PlusIcon } from "@heroicons/react/outline";
import ImageUploaderFormField from "./utils/ImageUploaderFormField";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Label } from "./utils/Common";
import Button from "../misc/Button";
import TabNav from "../pages/dashboard/general/TabNav";
import debounce from "lodash/debounce";
import LoadingSpinner from "../LoadingSpinner";
import { useCourseOptions } from "../../features/course/courseSlice";
import GroupKeywordsManager from "./GroupKeywordsManager";

function ReorderableNextStepItems({
  contents,
  handleReorder,
  error,
  setState,
  isDraggingNextStepItem,
  setIsDraggingNextStepItem
}) {
  const onDragEnd = val => {
    if (!val.destination) return;
    const fvsCloned = Array.from(contents);
    const [reordered] = fvsCloned.splice(val.source.index, 1);
    fvsCloned.splice(val.destination.index, 0, reordered);
    handleReorder(fvsCloned);
    setIsDraggingNextStepItem(false);
  };

  return (
    <div>
      {!contents.length ? (
        <p>This group has no next steps items yet.</p>
      ) : (
        <DragDropContext onDragStart={() => setIsDraggingNextStepItem(true)} onDragEnd={onDragEnd}>
          <Droppable
            droppableId="reorder-content"
            renderClone={(provided, snapshot, rubric) => {
              return (
                <div
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  ref={provided.innerRef}
                  style={provided.draggableProps.style}
                  className="border rounded-md border-gray-300 mt-2 py-6 px-4 bg-white"
                >
                  <NextStepsItemForm
                    state={contents[rubric.source.index]}
                    error={error}
                    setState={setState}
                    i={rubric.source.index}
                    isDraggingNextStepItem={isDraggingNextStepItem}
                  />
                </div>
              );
            }}
          >
            {provided => (
              <ul {...provided.droppableProps} ref={provided.innerRef}>
                {contents.map((nextStepItem, idx) => (
                  <Draggable key={idx} draggableId={`draggable-${idx}`} index={idx}>
                    {provided => (
                      <div
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                        style={provided.draggableProps.style}
                        className="border rounded-md border-gray-300 mt-2 py-6 px-4 bg-white"
                      >
                        <NextStepsItemForm
                          state={nextStepItem}
                          error={error}
                          setState={setState}
                          i={idx}
                          isDraggingNextStepItem={isDraggingNextStepItem}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}

function PricingPlanForm({ plan, onChange, onRemove, index, usedIntervals }) {
  const handlePriceChange = e => {
    const rawValue = e.target.value;
    if (!rawValue) return onChange({ target: { name: "price", value: 0 } });

    const cents = Math.round(parseFloat(rawValue) * 100);
    if (isNaN(cents)) return;

    onChange({
      target: {
        name: "price",
        value: cents,
        displayValue: rawValue
      }
    });
  };

  const intervals = [
    { value: "month", label: "Monthly" },
    { value: "year", label: "Annual" },
    { value: "one_time", label: "One-time" }
  ];

  return (
    <div className="flex flex-row justify-between gap-4 border rounded-md p-4 mb-4">
      <div className="w-full">
        <Label htmlFor={`price-${index}`} tooltipContent="Price in USD">
          Price
        </Label>
        <input
          id={`price-${index}`}
          name="price"
          className="border-gray-300 focus:shadow-outline-blue focus:border-blue-300 block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          placeholder="0.00"
          value={plan.displayValue || (plan.price ? (plan.price / 100).toFixed(2) : "")}
          onChange={handlePriceChange}
        />
      </div>
      <div className="w-full">
        <Label htmlFor={`interval-${index}`} tooltipContent="How often the user will be charged">
          Plan Interval
        </Label>
        <select
          name="interval"
          id={`interval-${index}`}
          value={plan.interval}
          onChange={onChange}
          className="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 border border-gray-300"
        >
          {intervals.map(interval => (
            <option
              key={interval.value}
              value={interval.value}
              disabled={usedIntervals.includes(interval.value) && interval.value !== plan.interval}
            >
              {interval.label}
            </option>
          ))}
        </select>
      </div>
      <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-800">
        Remove
      </button>
    </div>
  );
}

export function AboutContentSection({ state, setState }) {
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const { flashErrorNotification } = useToastNotifications();
  const [existingContent, setExistingContent] = useState(null);

  // Fetch existing content when component mounts
  useEffect(() => {
    const fetchContent = async () => {
      if (!state.aboutContentId) return;
      setIsLoading(true);
      try {
        const response = await axios.get(`/api/content/${state.aboutContentId}`);
        setExistingContent(response.data);
      } catch (error) {
        console.error("Error fetching content:", error);
        flashErrorNotification();
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, [state.aboutContentId, flashErrorNotification]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async term => {
        if (term.length < 2) return;
        try {
          const response = await axios.get("/api/admin/contents", {
            params: { searchTerm: term }
          });
          setSearchResults(response.data.items);
        } catch (error) {
          console.error("Error searching contents:", error);
          flashErrorNotification();
        }
      }, 300),
    [flashErrorNotification]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const handleContentSearch = event => {
    setSearchTerm(event.target.value);
    setSelectedContent(null);
  };

  const handleContentSelect = content => {
    setSelectedContent(content);
    setState(prevState => ({ ...prevState, aboutContentId: content.id }));
  };

  const handleContentRemove = () => {
    setExistingContent(null);
    setSelectedContent(null);
    setState(prevState => ({ ...prevState, aboutContentId: null }));
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-full py-8">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="mt-4">
      {existingContent || selectedContent ? (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <Label>Current About Content</Label>
            <button
              onClick={handleContentRemove}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Remove
            </button>
          </div>
          <div className="p-4 bg-gray-50 rounded-md">
            <h3 className="font-medium">{existingContent?.title || selectedContent?.title}</h3>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <Label htmlFor="aboutContentId">About Content</Label>
            <p className="text-sm text-gray-500 mb-2">
              Search for existing content to use as the about section.
            </p>
            <input
              type="text"
              id="aboutContentId"
              autoFocus
              name="aboutContentId"
              onChange={handleContentSearch}
              value={searchTerm}
              className="border-gray-300 focus:shadow-outline-blue focus:border-blue-300 block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            />
          </div>

          {searchResults.length > 0 && (
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-2">Select content:</p>
              {searchResults.map(content => (
                <Button
                  key={content.id}
                  variant="secondary"
                  className="mb-2 w-full text-slate-700 bg-white hover:text-slate-500"
                  onClick={() => handleContentSelect(content)}
                >
                  {content.title}
                </Button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const GroupForm = ({ model, closeModal }) => {
  const { admins } = useSelector(state => state.user);
  const { platformData } = useSelector(state => state.frontend);
  const { currentUser } = useSelector(state => state.auth);
  const location = useLocation();
  const history = useHistory();
  const courseOptions = useCourseOptions();

  const tabs = ["Details", "Pages"];
  const isStripeConnectEnabled = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (isStripeConnectEnabled) tabs.push("Pricing");

  // Get tab from URL or default to Details
  const params = new URLSearchParams(location.search);
  const [tab, setTab] = useState(params.get("tab") || "Details");

  // Update tab when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newTab = params.get("tab") || "Details";
    if (newTab !== tab) setTab(newTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleTabChange = newTab => {
    const params = new URLSearchParams(location.search);
    params.set("tab", newTab);
    history.replace({ search: params.toString() });
  };

  const isAdmin = currentUser.role === "admin";
  const defaultState = {
    groupCreatorId: currentUser.id,
    adminIds: [currentUser.id],
    GroupNextStepItems: [],
    CourseIds: [],
    communityCategories: "Wins,Questions",
    PlatformId: currentUser.PlatformId
  };
  if (model) model.CourseIds = model.Courses?.map(c => c.id) || [];

  const [state, setState] = useState(model || defaultState); // form state
  const { flashSuccessNotification, flashErrorNotification } = useToastNotifications();

  const { error } = useSelector(state => state.group);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const hasPricing = state.PricingPlans && state.PricingPlans.length > 0;
  const purchaseLink = hasPricing
    ? `${window.location.origin}/group/about/${state.id}`
    : state.purchaseLink;

  const onChange = event => {
    const { name, value } = event.target;
    if (error && error[name]) dispatch(clearError({ field: name }));
    setState(prevState => ({ ...prevState, [name]: value }));
  };

  const onSubmit = async event => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const group = { ...state };
      if (group.name) group.slug = slugify(group.name, { lower: true, strict: true });
      delete group.PricingPlans;
      delete group.Users;
      delete group.Courses;

      // Remove isTodayTabEnabled field to prevent overwriting the existing value
      // This is a deprecated feature that should not be modified by the form
      delete group.isTodayTabEnabled;

      // Set purchaseLink to about page if pricing is enabled
      if (hasPricing) group.purchaseLink = purchaseLink;

      const action = await dispatch(upsertGroupAsync({ group }));
      if (action.error) return flashErrorNotification();

      if (group.id && isStripeConnectEnabled && state.PricingPlans && currentUser.stripeAccountId) {
        await axios.post("/api/stripe/group-prices", {
          groupId: group.id,
          plans: state.PricingPlans
        });
      }
      await dispatch(readMyGroupsAsync());
      flashSuccessNotification();
      closeModal();
    } catch (error) {
      console.error("Failed to save group", error);
      flashErrorNotification();
    } finally {
      setIsSubmitting(false);
    }
  };

  // if the form is opened from the groups page (LeftNavbar)
  // the admins are not preloaded, so we load them manually here
  useEffect(() => {
    // no need to fetch if creator makes group because they're set as the admin
    if (isAdmin) dispatch(getAdminsAsync({ PlatformId: currentUser.PlatformId }));
  }, [dispatch, currentUser.PlatformId, isAdmin]);

  const nextStepItemsContainerRef = useRef(null);

  const [isDraggingNextStepItem, setIsDraggingNextStepItem] = useState(false);

  const handleReorder = newOrder => {
    // UPDATE THE ORDER
    const GroupNextStepItems = newOrder.map((item, i) => ({ ...item, order: i }));
    setState(state => ({ ...state, GroupNextStepItems }));
  };

  return (
    <form onSubmit={onSubmit} id="group-form">
      <TabNav tabs={tabs} activeTab={tab} onChange={handleTabChange} />
      {tab === "Details" && (
        <div className="mt-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <div className="mt-1 rounded-md shadow-sm relative">
              <input
                id="name"
                autoFocus
                name="name"
                className={`${
                  error && error.name
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.name) || ""}
                onChange={event => onChange(event)}
                placeholder="name"
              />
              {error && error.name && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
            {error && error.name && (
              <p className="mt-2 text-sm text-red-600">This field is required</p>
            )}
          </div>

          <div className="my-6">
            <Label htmlFor="description">Description</Label>
            <div className="mt-1 rounded-md shadow-sm relative">
              <textarea
                id="description"
                name="description"
                className={`${
                  error && error.name
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.description) || ""}
                onChange={event => onChange(event)}
                placeholder="description"
              />
              {error && error.description && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
          </div>

          <ImageUploaderFormField
            title="Cover Image"
            field="coverImg"
            value={state.coverImg}
            onChange={value => onChange({ target: { name: "coverImg", value } })}
            description={"SVG, PNG, JPG, GIF up to 10MB"}
          />

          {isAdmin && (
            <div className="mt-6">
              <Label htmlFor="adminIds">Group Admins</Label>
              <div className="mt-1 rounded-md shadow-sm relative">
                <Multiselect
                  id="adminIds"
                  name="adminIds"
                  placeholder="Choose admins"
                  onChange={onChange}
                  value={state.adminIds || []}
                  options={admins.map(a => ({ id: a.id, label: a.name }))}
                />
              </div>
              {error && error.adminIds && (
                <p className="mt-2 text-sm text-red-600">This field is required</p>
              )}
            </div>
          )}

          <div className="mt-6">
            <Label htmlFor="visibility">Visibility</Label>
            <div className="mt-1 rounded-md shadow-sm">
              <select
                id="visibility"
                className="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 border border-gray-300"
                name="visibility"
                value={(state && state.visibility) || ""}
                onChange={event => onChange(event)}
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <Label htmlFor="headlineBtnText" tooltipContent="Web only component.">
              CTA Button Text
            </Label>
            <div className="mt-1 rounded-md shadow-sm relative">
              <input
                id="headlineBtnText"
                name="headlineBtnText"
                className={`${
                  error && error.headlineBtnText
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.headlineBtnText) || ""}
                onChange={event => onChange(event)}
              />
              {error && error.headlineBtnText && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
          </div>
          <div className="mt-6">
            <Label htmlFor="headlineBtnUrl">CTA Button URL</Label>
            <div className="mt-1 rounded-md shadow-sm relative">
              <input
                id="headlineBtnUrl"
                name="headlineBtnUrl"
                className={`${
                  error && error.headlineBtnUrl
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.headlineBtnUrl) || ""}
                onChange={event => onChange(event)}
              />
              {error && error.headlineBtnUrl && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <Label
              htmlFor="purchaseLink"
              label="Purchase link"
              tooltipContent={
                hasPricing
                  ? "Purchase page is automatically set to the group's about page when pricing is enabled"
                  : "Add an link to your sales funnel or landing page. When a user clicks on this group they will be redirected to the URL provided. If empty, user will be directed to the group."
              }
            />
            <div className="mt-1 rounded-md shadow-sm relative">
              <input
                id="purchaseLink"
                name="purchaseLink"
                className={`${
                  error && error.name
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={purchaseLink || ""}
                onChange={event => onChange(event)}
                placeholder="https://purchasehere.com"
                disabled={hasPricing}
              />
              {/* {hasPricing && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ClipboardIcon className="w-5 text-blue-600" />
                </div>
              )} */}
              {error && error.purchaseLink && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
            {hasPricing && (
              <p className="mt-2 text-sm text-blue-600">Purchase page powered by Tribe</p>
            )}
          </div>
          <div className="mt-6">
            <Label
              htmlFor="externalGroupUrl"
              label="Group Redirect URL"
              tooltipContent="If your group is hosted on a platform outside of Tribe (e.g. Facebook, Discord), enter the URL here. Users will be redirected to this URL when they visit your group page."
            />
            <div className="mt-1 rounded-md shadow-sm relative">
              <input
                id="externalGroupUrl"
                name="externalGroupUrl"
                className={`${
                  error && error.externalGroupUrl
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.externalGroupUrl) || ""}
                onChange={event => onChange(event)}
                placeholder="https://www.facebook.com/groups/yourgroupname"
              />
              {error && error.externalGroupUrl && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
          </div>

          <div className="my-6">
            <Label htmlFor="position">Order</Label>
            <div className="mt-1 rounded-md shadow-sm relative">
              <input
                id="position"
                name="position"
                type="number"
                className={`${
                  error && error.position
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.position) || ""}
                onChange={event => onChange(event)}
                placeholder="0"
              />
              {error && error.position && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {tab === "Pages" && (
        <div className="mt-4">
          <div>
            <div className="grid gap-4 md:grid-cols-2 mb-4">
              <ToggleTab
                id="isCommunityTabEnabled"
                title="Community"
                description={
                  'Allow members to connect with each other and share wins or ask questions. Users can "go live" and connect live with other members.'
                }
                isEnabled={state.isCommunityTabEnabled}
                toggleSetting={() => {
                  setState(prevState => ({
                    ...prevState,
                    isCommunityTabEnabled: !state.isCommunityTabEnabled
                  }));
                }}
              >
                {state.isCommunityTabEnabled && platformData.firebaseCredentials && (
                  <div className="mt-4">
                    <Label
                      htmlFor="communityCategories"
                      tooltipContent="Manage categories for the community tab"
                    >
                      Categories
                    </Label>
                    <GroupKeywordsManager groupId={state.id} />
                  </div>
                )}
              </ToggleTab>

              {platformData.firebaseCredentials && (
                <ToggleTab
                  id="isCommunityAdminOnlyTabEnabled"
                  title="Announcements"
                  description="Similar layout to the feed tab but displays group admin posts only. Read-only for members."
                  isEnabled={state.isCommunityAdminOnlyTabEnabled}
                  toggleSetting={() => {
                    setState(prevState => ({
                      ...prevState,
                      isCommunityAdminOnlyTabEnabled: !state.isCommunityAdminOnlyTabEnabled
                    }));
                  }}
                />
              )}

              <ToggleTab
                id="isTrainingTabEnabled"
                title="Training"
                description="Our legacy version for a single course in your group. Organized with Collections and Content. "
                isEnabled={state.isTrainingTabEnabled}
                toggleSetting={() => {
                  setState(prevState => ({
                    ...prevState,
                    isTrainingTabEnabled: !state.isTrainingTabEnabled
                  }));
                }}
              />

              {platformData.firebaseCredentials && (
                <ToggleTab
                  id="isCoursesTabEnabled"
                  title="Courses"
                  description="Our latest version of Courses allows for multiple courses in a single group and a much better desktop and mobile experience. "
                  isEnabled={state.isCoursesTabEnabled}
                  toggleSetting={() => {
                    setState(prevState => ({
                      ...prevState,
                      isCoursesTabEnabled: !state.isCoursesTabEnabled
                    }));
                  }}
                >
                  {state.isCoursesTabEnabled && (
                    <div className="mt-6">
                      <Label htmlFor="CourseIds">Courses</Label>
                      <Multiselect
                        id="CourseIds"
                        name="CourseIds"
                        options={courseOptions}
                        value={state.CourseIds}
                        onChange={event => onChange(event)}
                      />
                    </div>
                  )}
                </ToggleTab>
              )}

              <ToggleTab
                id="isReplayTabEnabled"
                title="Replay"
                description="List weekly live call replays for your members. Content assigned to a group but not a collection will be listed."
                isEnabled={state.isReplayTabEnabled}
                toggleSetting={() => {
                  setState(prevState => ({
                    ...prevState,
                    isReplayTabEnabled: !state.isReplayTabEnabled
                  }));
                }}
              />

              {platformData.firebaseCredentials && (
                <ToggleTab
                  id="isWeeklyTabEnabled"
                  title="Weekly Tasks"
                  description={
                    'Build great weekly habits by displaying upcoming calls and tasks in a weekly calendar. Users can mark items completed and complete Daily "Rings" similar to Apple Fitness.'
                  }
                  isEnabled={state.isWeeklyTabEnabled}
                  toggleSetting={() => {
                    setState(prevState => ({
                      ...prevState,
                      isWeeklyTabEnabled: !state.isWeeklyTabEnabled
                    }));
                  }}
                />
              )}

              <ToggleTab
                id="isEventsTabEnabled"
                title="Events"
                description="Connect a 3rd party embedded calendar to show all upcoming events for your group. We recommend AddEvent.com"
                isEnabled={state.isEventsTabEnabled}
                toggleSetting={() => {
                  setState(prevState => ({
                    ...prevState,
                    isEventsTabEnabled: !state.isEventsTabEnabled
                  }));
                }}
              >
                {state.isEventsTabEnabled && (
                  <div className="mt-6 mb-4">
                    <Label htmlFor="eventUrl">Event Embed URL</Label>
                    <div className="mt-1 rounded-md shadow-sm relative">
                      <input
                        id="eventUrl"
                        name="eventUrl"
                        className={`${
                          error && error.eventUrl
                            ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                            : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                        } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                        value={(state && state.eventUrl) || ""}
                        onChange={event => onChange(event)}
                      />
                      {error && error.eventUrl && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <ExclamationCircleIcon className="w-5 text-red-600" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </ToggleTab>

              <ToggleTab
                id="isMembersTabEnabled"
                title="Members"
                description="Members can search and view all members in your group."
                isEnabled={state.isMembersTabEnabled}
                toggleSetting={() => {
                  setState(prevState => ({
                    ...prevState,
                    isMembersTabEnabled: !state.isMembersTabEnabled
                  }));
                }}
              />

              {platformData.firebaseCredentials && (
                <ToggleTab
                  id="isChatbotTabEnabled"
                  title="Chatbot"
                  description="Connect a 3rd party chatbot to a dedicated tab in your group. We recommend Chatbase.co."
                  isEnabled={state.isChatbotTabEnabled}
                  toggleSetting={() => {
                    setState(prevState => ({
                      ...prevState,
                      isChatbotTabEnabled: !state.isChatbotTabEnabled
                    }));
                  }}
                >
                  {state.isChatbotTabEnabled && (
                    <div>
                      <div className="my-6">
                        <Label htmlFor="chatbotTabLabel">Chatbot Tab Label</Label>
                        <div className="mt-1 rounded-md shadow-sm relative">
                          <input
                            id="chatbotTabLabel"
                            name="chatbotTabLabel"
                            className={`${
                              error && error.chatbotTabLabel
                                ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                                : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                            } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                            value={(state && state.chatbotTabLabel) || ""}
                            onChange={event => onChange(event)}
                          />
                          {error && error.chatbotTabLabel && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <ExclamationCircleIcon className="w-5 text-red-600" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4">
                        <Label htmlFor="chatbotEmbedUrl">Chatbot Embed Url</Label>
                        <div className="mt-1 rounded-md shadow-sm relative">
                          <input
                            id="chatbotEmbedUrl"
                            name="chatbotEmbedUrl"
                            className={`${
                              error && error.chatbotEmbedUrl
                                ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                                : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                            } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                            value={(state && state.chatbotEmbedUrl) || ""}
                            onChange={event => onChange(event)}
                          />
                          {error && error.chatbotEmbedUrl && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <ExclamationCircleIcon className="w-5 text-red-600" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </ToggleTab>
              )}
            </div>

            <ToggleTab
              id="isNextStepsTabEnabled"
              title="Next Steps"
              description="Display important next steps or links for your members. "
              isEnabled={state.isNextStepsTabEnabled}
              toggleSetting={() => {
                setState(prevState => ({
                  ...prevState,
                  isNextStepsTabEnabled: !state.isNextStepsTabEnabled
                }));
              }}
            >
              {state.isNextStepsTabEnabled && (
                <div>
                  {/* NEXT STEPS ITEMS */}
                  <div className="mt-6"></div>
                  <Label>Next step items:</Label>

                  <div className="max-h-[700px] overflow-auto mb-4" ref={nextStepItemsContainerRef}>
                    <ReorderableNextStepItems
                      contents={state.GroupNextStepItems || []}
                      setState={setState}
                      handleReorder={handleReorder}
                      error={error}
                      isDraggingNextStepItem={isDraggingNextStepItem}
                      setIsDraggingNextStepItem={setIsDraggingNextStepItem}
                    />
                  </div>

                  <div className="mt-2">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-slate-700 bg-white hover:text-slate-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 transition duration-150 ease-in-out"
                      onClick={() => {
                        setTimeout(() => {
                          if (nextStepItemsContainerRef?.current) {
                            // scroll to the bottom of the Next Step Items container after a little bit
                            const nextStepItemHeight = 400;
                            nextStepItemsContainerRef.current.scrollTop =
                              nextStepItemsContainerRef.current.scrollHeight + nextStepItemHeight;
                          }
                        }, 500);

                        // push a new item
                        setState(state => ({
                          ...state,
                          GroupNextStepItems: [
                            ...state.GroupNextStepItems,
                            {
                              order:
                                state.GroupNextStepItems?.length > 0
                                  ? Math.max(...state.GroupNextStepItems.map(item => item.order)) +
                                    1
                                  : 1
                            }
                          ]
                        }));
                      }}
                    >
                      <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                      Add new item
                    </button>
                  </div>
                  {/* END: NEXT STEPS ITEMS */}
                </div>
              )}
            </ToggleTab>
          </div>
        </div>
      )}
      {tab === "Pricing" && (
        <PricingTab currentUser={currentUser} state={state} setState={setState} />
      )}
      <div className="mt-8 grid grid-cols-2 gap-3">
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>

        <Button type="submit" variant="primary" disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </form>
  );
};

function StripeAccountManagement({ stripeAccountId, onStatusChange = () => {} }) {
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(false);
  const [isAccountVerified, setIsAccountVerified] = useState(false);
  const [isVerificationPending, setIsVerificationPending] = useState(false);

  useEffect(() => {
    const checkAccountStatus = async () => {
      if (!stripeAccountId) return;
      setLoading(true);
      try {
        const response = await axios.get("/api/stripe/account");
        const { account } = response.data;
        const isAccountVerified =
          account?.charges_enabled && account?.details_submitted && account?.payouts_enabled;
        const isVerificationPending = account?.verification?.pending_fields?.length > 0;
        setIsAccountVerified(isAccountVerified);
        setIsVerificationPending(isVerificationPending);
        onStatusChange(isAccountVerified);
      } catch (error) {
        console.error("Failed to fetch Stripe account status:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    checkAccountStatus();
  }, [stripeAccountId, onStatusChange]);

  async function handleButtonClick() {
    setRedirecting(true);
    setError(false);
    try {
      if (!stripeAccountId) await axios.post("/api/stripe/account");
      const queryParams = new URLSearchParams(window.location.search);
      await redirectToStripeAccountManagement(queryParams.toString());
    } catch (error) {
      console.error("Failed to create / update Stripe account.", error);
      setError(true);
    } finally {
      setRedirecting(false);
    }
  }

  async function redirectToStripeAccountManagement(queryParams) {
    const response = await axios.post("/api/stripe/account_link", {
      account: stripeAccountId,
      queryParams
    });
    const { url } = response.data;
    if (url) window.location.href = url;
  }

  return (
    <div id="stripe-account-management" className="mt-3 mb-4">
      <h2 className="text-lg font-semibold mb-3">Your Stripe Seller Account</h2>

      {loading && <LoadingSpinner size="sm" />}
      {!loading && stripeAccountId && !isAccountVerified && !isVerificationPending && (
        <p className="text-red-600 mb-3">
          Your Stripe account needs additional verification before you can set up pricing. Please
          update your bank information.
        </p>
      )}
      {!loading && stripeAccountId && !isAccountVerified && isVerificationPending && (
        <p className="text-blue-600 mb-3 flex items-center">
          <ClockIcon className="w-4 mr-1 text-blue-600" /> Your Stripe account verification is
          pending. Please come back later.
        </p>
      )}

      {!loading && !redirecting && stripeAccountId && isAccountVerified && (
        <p className="flex items-center">
          <CheckCircleIcon className="w-4 mr-1 text-green-500" /> Stripe account valid.&nbsp;
          <span className="text-blue-500 cursor-pointer" onClick={handleButtonClick}>
            Update bank info!
          </span>
        </p>
      )}
      {!loading &&
        !redirecting &&
        (!stripeAccountId || (!isAccountVerified && !isVerificationPending)) && (
          <>
            <Button type="button" onClick={handleButtonClick}>
              {stripeAccountId ? "Update bank info!" : "Create an account!"}
            </Button>
            <p className="mb-2 text-sm text-gray-600">You will be taken to Stripe.</p>
          </>
        )}
      {error && <p className="error">Something went wrong!</p>}
      {!loading && redirecting && <p className="text-sm text-gray-600">Redirecting to Stripe...</p>}
    </div>
  );
}

function PricingTab({ currentUser, state, setState }) {
  const { flashSuccessNotification } = useToastNotifications();
  const [isAccountVerified, setIsAccountVerified] = useState(false);

  if (state.pricingManagerUserId && state.pricingManagerUserId !== currentUser.id) {
    return (
      <div className="mt-4">
        <p>This group is connected to a different Stripe account.</p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <h2 className="text-lg font-semibold mb-4">Group Landing Page</h2>
      <div className="mb-6 flex flex-row gap-4">
        <Link
          to={`/group/about/${state.id}`}
          target="_blank"
          className="text-sm text-dark-blue-200 font-semibold flex items-center"
        >
          <InformationCircleIcon className="w-4 mr-1" />
          Visit
        </Link>
        <button
          type="button"
          className="text-sm text-dark-blue-200 font-semibold flex items-center"
          onClick={() => {
            const groupUrl = `${window.location.origin}/group/about/${state.id}`;
            navigator.clipboard.writeText(groupUrl);
            flashSuccessNotification("Link copied to clipboard");
          }}
        >
          <ClipboardIcon className="w-4 mr-1" />
          Copy Link
        </button>
      </div>

      <AboutContentSection state={state} setState={setState} />

      <StripeAccountManagement
        stripeAccountId={currentUser.stripeAccountId}
        onStatusChange={setIsAccountVerified}
      />

      {isAccountVerified && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-2">Pricing Plans</h2>

          {(state.PricingPlans || []).map((plan, index) => {
            const usedIntervals = state.PricingPlans.filter((_, i) => i !== index).map(
              p => p.interval
            );

            return (
              <PricingPlanForm
                key={index}
                index={index}
                plan={plan}
                usedIntervals={usedIntervals}
                onChange={e => {
                  const plans = [...state.PricingPlans];
                  plans[index] = { ...plans[index], [e.target.name]: e.target.value };
                  const isPriceUpdated = e.target.name === "price";
                  if (isPriceUpdated) {
                    plans[index].displayValue = e.target.displayValue;
                    delete plans[index].id;
                  }
                  setState(prev => ({ ...prev, PricingPlans: plans }));
                }}
                onRemove={() => {
                  const plans = state.PricingPlans.filter((_, i) => i !== index);
                  setState(prev => ({ ...prev, PricingPlans: plans }));
                }}
              />
            );
          })}

          {/* Only show Add button if we haven't used all interval types */}
          {(!state.PricingPlans || state.PricingPlans.length < 3) && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const usedIntervals = (state.PricingPlans || []).map(p => p.interval);
                const availableIntervals = ["month", "year", "one_time"].filter(
                  i => !usedIntervals.includes(i)
                );

                setState(prev => ({
                  ...prev,
                  PricingPlans: [
                    ...(prev.PricingPlans || []),
                    { interval: availableIntervals[0], price: 0 }
                  ]
                }));
              }}
            >
              Add Pricing Plan
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ToggleTab({ id, title, description, isEnabled, toggleSetting, children }) {
  return (
    <div
      key={id}
      className={`bg-white rounded-lg shadow p-4 transition-opacity duration-200 ${
        isEnabled ? "opacity-100" : "opacity-50"
      }`}
    >
      <div className="">
        <div className="flex justify-between mb-2">
          <h3 className="text-lg font-medium">{title}</h3>
          <button
            onClick={e => {
              e.preventDefault();
              toggleSetting(id);
            }}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:bg-blue-500 focus:ring-offset-2 ${
              isEnabled ? "bg-blue-500" : "bg-gray-200"
            }`}
            role="switch"
            aria-checked={isEnabled}
            data-testid={`${id}-toggle`}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                isEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      {children}
    </div>
  );
}

export default GroupForm;
